import React, { useEffect, useState } from "react";
import tokenService from "../../../_services/token.service"; // 👈 add this
import { config } from "../../../_config/config";            // 👈 optional, see below

const STATUS_COLORS = {
    ok: "#2e7d32",
    degraded: "#f9a825",
    down: "#c62828",
};

const CHECK_STATUS_COLORS = {
    ok: "#2e7d32",
    warning: "#f9a825",
    error: "#c62828",
};

function StatusBadge({ status }) {
    const color = STATUS_COLORS[status] || "#757575";
    const label =
        status === "ok"
            ? "All systems operational"
            : status === "degraded"
                ? "Degraded"
                : status === "down"
                    ? "Some systems down"
                    : "Unknown";

    return (
        <div
            style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "8px 12px",
                borderRadius: "999px",
                backgroundColor: "#f5f5f5",
                border: `1px solid ${color}`,
                gap: "8px",
            }}
        >
            <span
                style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: color,
                }}
            />
            <span style={{ color, fontWeight: 600 }}>{label}</span>
        </div>
    );
}

function CheckRow({ check }) {
    const color = CHECK_STATUS_COLORS[check.status] || "#757575";
    const icon =
        check.status === "ok"
            ? "●"
            : check.status === "warning"
                ? "▲"
                : check.status === "error"
                    ? "✖"
                    : "●";

    return (
        <tr>
            <td style={{ padding: "8px 12px", fontWeight: 500 }}>{check.name}</td>
            <td style={{ padding: "8px 12px" }}>
                <span style={{ color, fontWeight: 600 }}>
                    {icon} {check.status.toUpperCase()}
                </span>
                {check.critical && (
                    <span
                        style={{
                            marginLeft: 8,
                            fontSize: 12,
                            padding: "2px 6px",
                            borderRadius: 10,
                            backgroundColor: "#ffebee",
                            color: "#c62828",
                        }}
                    >
                        Critical
                    </span>
                )}
            </td>
            <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                {check.latency_ms != null ? `${check.latency_ms} ms` : "—"}
            </td>
            <td style={{ padding: "8px 12px", fontSize: 13, color: "#555" }}>
                {check.message}
            </td>
        </tr>
    );
}

export default function StatusPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadStatus = () => {
        setLoading(true);
        setError(null);

        const token = tokenService.getLocalAccessToken();
        // if your API base lives in config.SERVER_URL (like other admin calls):
        // const url = `${config.SERVER_URL}admin/status/`;
        // if you mounted it at /api/admin/status/ on the same host:
        const url = "/api/admin/status/";

        fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        })
            .then(async (res) => {
                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(`HTTP ${res.status}: ${text}`);
                }
                return res.json();
            })
            .then((json) => {
                setData(json);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Status error", err);
                setError(err.message);
                setLoading(false);
            });
    };

    useEffect(() => {
        loadStatus();
    }, []);

    return (
        <div style={{ padding: 24 }}>
            <div
                style={{
                    marginBottom: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                }}
            >
                <div>
                    <h1 style={{ margin: 0, fontSize: 24 }}>System Status</h1>
                    <p style={{ marginTop: 4, color: "#616161" }}>
                        Quick overview of core services (database, Celery, Google Maps, etc.)
                    </p>
                </div>
                <button
                    onClick={loadStatus}
                    disabled={loading}
                    style={{
                        padding: "8px 16px",
                        borderRadius: 6,
                        border: "1px solid #1976d2",
                        backgroundColor: loading ? "#e3f2fd" : "#1976d2",
                        color: loading ? "#1976d2" : "#fff",
                        fontWeight: 600,
                        cursor: loading ? "default" : "pointer",
                    }}
                >
                    {loading ? "Checking…" : "Re-run checks"}
                </button>
            </div>

            {error && (
                <div
                    style={{
                        marginBottom: 16,
                        padding: 12,
                        borderRadius: 4,
                        backgroundColor: "#ffebee",
                        color: "#c62828",
                    }}
                >
                    Failed to load status: {error}
                </div>
            )}

            {data && (
                <>
                    <div style={{ marginBottom: 24 }}>
                        <StatusBadge status={data.overall_status} />
                        <div style={{ marginTop: 8, fontSize: 12, color: "#757575" }}>
                            Last checked:{" "}
                            {new Date(data.last_checked).toLocaleString(undefined, {
                                dateStyle: "short",
                                timeStyle: "medium",
                            })}
                        </div>
                    </div>

                    <div
                        style={{
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            overflow: "hidden",
                            backgroundColor: "#fff",
                        }}
                    >
                        <table
                            style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                fontSize: 14,
                            }}
                        >
                            <thead style={{ backgroundColor: "#fafafa" }}>
                            <tr>
                                <th
                                    style={{
                                        textAlign: "left",
                                        padding: "8px 12px",
                                        borderBottom: "1px solid #e0e0e0",
                                    }}
                                >
                                    Service
                                </th>
                                <th
                                    style={{
                                        textAlign: "left",
                                        padding: "8px 12px",
                                        borderBottom: "1px solid #e0e0e0",
                                    }}
                                >
                                    Status
                                </th>
                                <th
                                    style={{
                                        textAlign: "left",
                                        padding: "8px 12px",
                                        borderBottom: "1px solid #e0e0e0",
                                    }}
                                >
                                    Latency
                                </th>
                                <th
                                    style={{
                                        textAlign: "left",
                                        padding: "8px 12px",
                                        borderBottom: "1px solid #e0e0e0",
                                    }}
                                >
                                    Details
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {data.checks.map((check) => (
                                <CheckRow key={check.id} check={check} />
                            ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {loading && !data && <p>Loading status…</p>}
        </div>
    );
}
